import { GridApi, GridColDef, GridFilterModel, GridRowId } from '@mui/x-data-grid-premium';
import React from 'react';

const MAX_CONTEXT_ROWS = 30 as const;
const PIPE_ESCAPE_PATTERN = /\|/g;
const NEWLINE_PATTERN = /\n/g;

/**
 * @description Builds the table data section with pagination and filter state.
 *
 * @returns Markdown-formatted table section string if rows are present
 */
export function buildTableSection(
  apiRef: React.MutableRefObject<GridApi>,
  filterModel: GridFilterModel,
  currentPage: number,
  totalPages: number
): string {
  const sortedRowIds = apiRef.current.getSortedRowIds?.() ?? [];
  const visibleColumns = extractVisibleDataColumns(apiRef);
  const formattedFilters = formatFilterModel(filterModel);

  const isTruncated = sortedRowIds.length > MAX_CONTEXT_ROWS;
  const tableMarkdown = formatRowsAsMarkdownTable(
    visibleColumns,
    sortedRowIds,
    apiRef,
    MAX_CONTEXT_ROWS
  );

  const sections = [
    isTruncated ? `Only first ${MAX_CONTEXT_ROWS} of ${sortedRowIds.length} rows:` : '',
    `TABLE DATA: ${tableMarkdown}`,
    `CURRENT PAGE: ${currentPage} of ${totalPages}`,
    `APPLIED FILTERS: ${formattedFilters}`
  ];

  return sortedRowIds.length > 0 ? sections.join('\n') : '';
}

/**
 * @description Extracts visible columns excluding action type columns.
 *
 * @returns Array of GridColDef with header names
 */
function extractVisibleDataColumns(apiRef: React.MutableRefObject<GridApi>): GridColDef[] {
  return apiRef.current
    .getVisibleColumns()
    .filter((column) => column.type !== 'actions' && Boolean(column.headerName || column.field));
}

/**
 * @description Formats filter model items as human-readable query string.
 *
 * @returns "None" or "[field] operator "value" AND ..." string
 */
export function formatFilterModel(filterModel: GridFilterModel): string {
  if (filterModel.items.length === 0) return 'None';

  return filterModel.items
    .map((item) => `[${item.field}] ${item.operator} "${item.value}"`)
    .join(' AND ');
}

/**
 * @description Converts DataGrid rows and columns to GitHub-flavored Markdown table.
 *
 * @returns Markdown table string with escaped delimiters
 */
function formatRowsAsMarkdownTable(
  visibleColumns: GridColDef[],
  sortedRowIds: GridRowId[],
  apiRef: React.MutableRefObject<GridApi>,
  maxRows: number
): string {
  const headerRow = `| ${visibleColumns.map((col) => col.headerName || col.field).join(' | ')} |`;
  const separatorRow = `| ${visibleColumns.map(() => '---').join(' | ')} |`;

  const dataRows = sortedRowIds
    .slice(0, maxRows)
    .map((rowId) => formatSingleRow(rowId, visibleColumns, apiRef))
    .join('\n');

  return `${headerRow}\n${separatorRow}\n${dataRows}`;
}

/**
 * @description Formats a single DataGrid row as Markdown table row.
 *
 * @returns Pipe-delimited string with escaped values
 */
function formatSingleRow(
  rowId: GridRowId,
  visibleColumns: GridColDef[],
  apiRef: React.MutableRefObject<GridApi>
): string {
  const cellValues = visibleColumns.map((column) => {
    const cellParams = apiRef.current.getCellParams(rowId, column.field);
    const rawValue = cellParams.formattedValue ?? cellParams.value ?? '-';

    return sanitizeCellValue(rawValue);
  });

  return `| ${cellValues.join(' | ')} |`;
}

/**
 * @description Sanitizes cell value for Markdown table compatibility.
 *
 * @returns Escaped string safe for Markdown pipes
 */
function sanitizeCellValue(rawValue: unknown): string {
  const stringified = String(rawValue ?? '-');

  return (
    stringified.replace(PIPE_ESCAPE_PATTERN, '\\|').replace(NEWLINE_PATTERN, ' ').trim() || '-'
  );
}
